import { injectable, inject } from 'inversify';
import finsightConfig from '@finsight/config';
import { I18nInstance, type I18nInstanceType } from '@framework/i18n';
import { TMessageName, IMessageData, TMessageCode } from '@framework/message';
import finsightWebUrl from '@/finsight/infrastructure/finsightWebUrl';
import { IMessageStrategy, PlainMessageStrategy } from '@framework/message/MessageManager/Strategy';

@injectable()
class I18nMessageStrategy implements IMessageStrategy {
  constructor(
    @inject(I18nInstance) private i18nInstance: I18nInstanceType,
    @inject(PlainMessageStrategy) private plainMessageStrategy: PlainMessageStrategy,
  ) {}

  private getContactLinkTranslations() {
    const linkText = this.i18nInstance.t('LINK_CONTACT');
    const linkUsText = this.i18nInstance.t('LINK_CONTACT_US');
    const url = finsightWebUrl.getContactUsUrl();

    const contactLinkTranslations = {
      link: `<a href='${ url }' target="_blank" rel="noopener noreferrer">${ linkText }</a>`,
      link_us: `<a href='${ url }' target="_blank" rel="noopener noreferrer">${ linkUsText }</a>`,
      email: finsightConfig.supportEmail,
    };

    return contactLinkTranslations;
  }

  getMessageByName(name: TMessageName, data: IMessageData) {
    const shouldLocalize = this.i18nInstance.exists(name);

    if (!shouldLocalize) {
      return this.plainMessageStrategy.getMessageByName(name, data);
    }

    const contactLinkTranslations = this.getContactLinkTranslations();

    return this.i18nInstance.t(name, {
      ...contactLinkTranslations,
      ...data,
      interpolation: {
        escapeValue: false,
      },
    });
  }

  getMessageNameByErrorCode(code: TMessageCode): TMessageName {
    return this.plainMessageStrategy.getMessageNameByErrorCode(code);
  }

  getFallbackMessageName(): TMessageName {
    return this.plainMessageStrategy.getFallbackMessageName();
  }
}

export default I18nMessageStrategy;
